/**
 * Does the warehouse still agree with the database it mirrors?
 *
 * The sibling panel on this page answers "did the export run". It cannot answer
 * "is what it wrote correct" — and that is the failure that actually happened:
 * the export dropped a row at every page boundary for weeks while BigQuery
 * looked entirely healthy. Its charts were plausible and its totals were
 * plausible; there were simply seven fewer campaign-days than Supabase held. A
 * panel reading only BigQuery could never have caught that, which is why this
 * one reads both and shows the difference.
 *
 * @module components/admin/BigQueryReconcile
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Download, Loader2, RefreshCw } from "lucide-react";
import {
  fetchReconcileReport,
  type PlatformComparison,
  type ReconcileReport,
} from "@/services/bigQueryReconcileService";
import { downloadCsv } from "@/services/csv";
import { todayDataDate, shiftDataDate } from "@/services/dataDate";
import { useWorkspace } from "@/contexts/WorkspaceContext";

/**
 * Range choices, in days back from today.
 *
 * Anything past the warehouse's own extent is served short, so a choice that
 * cannot be honoured says so in its own label rather than quietly returning
 * less than it offered.
 */
const RANGES = [
  { days: 7, label: "7 วัน" },
  { days: 30, label: "30 วัน" },
  { days: 60, label: "60 วัน" },
  { days: 180, label: "180 วัน" },
] as const;

const int = (n: number): string => n.toLocaleString("en-US");
const baht = (n: number): string =>
  `฿${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** A delta cell: zero is the good case and should read as calm, not as data. */
const Delta: React.FC<{ value: number; format?: (n: number) => string }> = ({ value, format = int }) => {
  if (value === 0) return <span className="text-neutral-700">0</span>;
  return (
    <span className="font-semibold text-error-700">
      {value > 0 ? "+" : ""}
      {format(value)}
    </span>
  );
};

const PlatformRow: React.FC<{ row: PlatformComparison }> = ({ row }) => (
  <tr className="border-t border-neutral-100">
    <td className="py-2 pr-3 font-medium text-ink-900">{row.platform}</td>
    <td className="py-2 pr-3 text-right tabular-nums">{int(row.warehouse?.campaignDays ?? 0)}</td>
    <td className="py-2 pr-3 text-right tabular-nums">{int(row.source?.campaignDays ?? 0)}</td>
    <td className="py-2 pr-3 text-right tabular-nums">
      <Delta value={row.delta.campaignDays} />
    </td>
    <td className="py-2 pr-3 text-right tabular-nums">{baht(row.warehouse?.cost ?? 0)}</td>
    <td className="py-2 pr-3 text-right tabular-nums">{baht(row.source?.cost ?? 0)}</td>
    <td className="py-2 text-right tabular-nums">
      <Delta value={row.delta.cost} format={baht} />
    </td>
  </tr>
);

export const BigQueryReconcile: React.FC = () => {
  const { workspace } = useWorkspace();
  const [days, setDays] = useState<number>(30);
  const [report, setReport] = useState<ReconcileReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const requested = useMemo(() => {
    // Bangkok data calendar, like every other window in this app — a UTC
    // "today" puts the boundary a day out for seven hours each evening.
    const to = todayDataDate();
    return { from: shiftDataDate(to, -days), to };
  }, [days]);

  const load = useCallback(async () => {
    if (!workspace?.id) return;
    setLoading(true);
    setError(null);
    const result = await fetchReconcileReport({
      workspaceId: workspace.id,
      from: requested.from,
      to: requested.to,
    });
    if (result.success && result.data) setReport(result.data);
    else setError(result.error ?? "Could not compare the warehouse");
    setLoading(false);
  }, [workspace?.id, requested.from, requested.to]);

  useEffect(() => {
    void load();
  }, [load]);

  const mismatches = report?.platforms.filter((p) => !p.matches) ?? [];
  const window = report?.window ?? null;

  const handleExport = (): void => {
    if (!report || !window) return;
    const rows: Array<Array<string | number>> = [
      [
        "platform",
        "warehouse_campaign_days",
        "source_campaign_days",
        "delta_campaign_days",
        "warehouse_cost",
        "source_cost",
        "delta_cost",
        "warehouse_impressions",
        "source_impressions",
        "delta_impressions",
        "warehouse_clicks",
        "source_clicks",
        "delta_clicks",
        "warehouse_conversions",
        "source_conversions",
        "delta_conversions",
      ],
      ...report.platforms.map((p) => [
        p.platform,
        p.warehouse?.campaignDays ?? 0,
        p.source?.campaignDays ?? 0,
        p.delta.campaignDays,
        p.warehouse?.cost ?? 0,
        p.source?.cost ?? 0,
        p.delta.cost,
        p.warehouse?.impressions ?? 0,
        p.source?.impressions ?? 0,
        p.delta.impressions,
        p.warehouse?.clicks ?? 0,
        p.source?.clicks ?? 0,
        p.delta.clicks,
        p.warehouse?.conversions ?? 0,
        p.source?.conversions ?? 0,
        p.delta.conversions,
      ]),
    ];

    // A CSV leaves the app: whatever caveat sits beside these numbers on screen
    // stays behind, so the file has to carry it. Especially the clamp — a file
    // labelled "180 days" holding 60 is the failure this whole panel exists to
    // prevent.
    const provenance = [
      `Source: BigQuery ${report.workspace?.dataset ?? "?"} vs Supabase media_ad_set_metrics`,
      `Workspace: ${report.workspace?.name ?? "?"}`,
      `Requested: ${window.requestedFrom ?? "?"} to ${window.requestedTo ?? "?"}`,
      `Compared: ${window.from ?? "?"} to ${window.to ?? "?"}`,
      `Warehouse holds: ${window.warehouseFrom ?? "?"} to ${window.warehouseTo ?? "?"}`,
      `Exported: ${new Date().toISOString()}`,
    ];
    if (window.clampedStart) {
      provenance.push(
        `NOTE: the requested range starts before ${window.warehouseFrom}, which the warehouse no longer holds. ` +
          "BigQuery is a sandbox with a forced 60-day partition expiry, so older data lives only in " +
          "Supabase and is NOT in this file.",
      );
    }
    if (window.clampedEnd) {
      provenance.push(
        `NOTE: the warehouse's latest date is ${window.warehouseTo}; the range was trimmed to it. ` +
          "The export runs when a sync drains its queue, so the newest day or two can lag. Not data loss.",
      );
    }
    // Named so the file is self-describing in a Downloads folder.
    downloadCsv(`bq-reconcile_${window.from}_${window.to}.csv`, rows, { provenance });
  };

  if (!report && loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white p-4 text-sm text-neutral-700">
        <Loader2 size={16} className="animate-spin" />
        กำลังเทียบคลังข้อมูลกับฐานข้อมูล…
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-800">
        <div className="flex items-center gap-2 font-semibold">
          <AlertTriangle size={16} />
          เทียบข้อมูลไม่สำเร็จ
        </div>
        <p className="mt-1">{error}</p>
      </div>
    );
  }

  if (report && !report.configured) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-4 text-sm text-neutral-700">
        ยังไม่ได้ตั้งค่า BigQuery (ต้องมี <code>BQ_PROJECT</code> และ service-account key) — ไม่ใช่ข้อผิดพลาด
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-neutral-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-ink-900">คลังข้อมูลตรงกับฐานข้อมูลไหม</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm text-ink-900 hover:bg-neutral-50 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : undefined} />
            เทียบใหม่
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={!report || report.empty}
            className="inline-flex items-center gap-1.5 rounded-md bg-ink-950 px-2.5 py-1.5 text-sm font-semibold text-white hover:bg-ink-900 disabled:opacity-50"
          >
            <Download size={14} />
            ดาวน์โหลด CSV
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {RANGES.map((r) => {
          // A choice the warehouse cannot honour says so on the button, rather
          // than being served short in silence.
          const beyond =
            window?.warehouseFrom !== null &&
            window?.warehouseFrom !== undefined &&
            shiftDataDate(todayDataDate(), -r.days) < window.warehouseFrom;
          return (
            <button
              key={r.days}
              type="button"
              onClick={() => setDays(r.days)}
              className={`rounded-md border px-2.5 py-1 text-sm ${
                days === r.days
                  ? "border-ink-950 bg-ink-950 text-white"
                  : "border-neutral-300 text-ink-900 hover:bg-neutral-50"
              }`}
            >
              {r.label}
              {beyond && (
                <span className={days === r.days ? "text-neutral-700" : "text-neutral-700"}>
                  {" "}
                  (คลังมีถึง {window?.warehouseFrom})
                </span>
              )}
            </button>
          );
        })}
      </div>

      {window && (
        <p className="text-xs text-neutral-700">
          เทียบช่วง <strong className="text-ink-900">{window.from} ถึง {window.to}</strong>
          {/* Two different causes. Calling a one-day freshness lag "the 60-day
              expiry" would send someone to fix the wrong thing. */}
          {window.clampedStart && (
            <>
              {" "}— ขอตั้งแต่ {window.requestedFrom} แต่คลังเก็บย้อนหลังได้ถึง {window.warehouseFrom} เท่านั้น
              (partition หมดอายุ 60 วัน) ข้อมูลเก่ากว่านั้นอยู่ใน Supabase
            </>
          )}
          {window.clampedEnd && (
            <>
              {" "}— คลังอัปเดตถึง {window.warehouseTo} (export ทำงานตอน sync เสร็จ ไม่ใช่รายวัน)
            </>
          )}
        </p>
      )}

      {report?.empty ? (
        <p className="text-sm text-neutral-700">คลังยังไม่มีข้อมูลของ workspace นี้</p>
      ) : (
        <>
          <div
            role="status"
            className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
              mismatches.length === 0
                ? "bg-success-50 text-success-800"
                : "bg-error-50 font-semibold text-error-800"
            }`}
          >
            {mismatches.length === 0 ? (
              <>
                <CheckCircle2 size={16} />
                ตรงกันทุกแพลตฟอร์มในช่วงนี้ — คลังไม่ได้ทำข้อมูลหาย
              </>
            ) : (
              <>
                <AlertTriangle size={16} />
                ไม่ตรงกัน {mismatches.length} แพลตฟอร์ม: {mismatches.map((m) => m.platform).join(", ")}
              </>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[46rem] text-sm">
              <thead className="text-xs uppercase tracking-wide text-neutral-700">
                <tr>
                  <th className="py-1.5 pr-3 text-left font-medium">แพลตฟอร์ม</th>
                  <th className="py-1.5 pr-3 text-right font-medium">BQ วัน</th>
                  <th className="py-1.5 pr-3 text-right font-medium">DB วัน</th>
                  <th className="py-1.5 pr-3 text-right font-medium">ต่าง</th>
                  <th className="py-1.5 pr-3 text-right font-medium">BQ ใช้จ่าย</th>
                  <th className="py-1.5 pr-3 text-right font-medium">DB ใช้จ่าย</th>
                  <th className="py-1.5 text-right font-medium">ต่าง</th>
                </tr>
              </thead>
              <tbody>
                {report?.platforms.map((p) => (
                  <PlatformRow key={p.platform} row={p} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default BigQueryReconcile;
